import { __ } from '@wordpress/i18n';
import {
    PanelBody,
    RangeControl,
    SelectControl,
    ToggleControl,
    __experimentalNumberControl as NumberControl
} from '@wordpress/components';

/**
 * Get testimonial controls for the inspector panel
 * @param {Object} attributes - Block attributes
 * @param {Function} setAttributes - Function to update attributes
 * @param {String} prefix - Optional prefix for attribute names (e.g., 'testimonial_')
 * @returns {JSX.Element} - Control components
 */
export const TestimonialControls = ({ attributes, setAttributes, prefix = '' }) => {
    const getAttr = (name) => attributes[`${prefix}${name}`];
    const setAttr = (name, value) => setAttributes({ [`${prefix}${name}`]: value });

    return (
        <PanelBody 
            title={__('Testimonials Settings', 'puroclean')} 
            initialOpen={true}
        >
            <SelectControl
                label={__('Testimonial Type', 'puroclean')}
                value={getAttr('testimonialType') || 'client'}
                options={[
                    { label: __('Client Reviews', 'puroclean'), value: 'client' },
                    { label: __('Employee Reviews', 'puroclean'), value: 'employee' },
                    { label: __('All Reviews', 'puroclean'), value: 'all' }
                ]}
                onChange={(value) => setAttr('testimonialType', value)}
                help={__('Choose which type of testimonials to display', 'puroclean')}
            />

            <RangeControl
                label={__('Number of Testimonials', 'puroclean')}
                value={getAttr('testimonialCount') || 3}
                onChange={(value) => setAttr('testimonialCount', value)}
                min={1}
                max={10}
                help={__('How many testimonials to fetch and display', 'puroclean')}
            />

            <SelectControl
                label={__('Layout Style', 'puroclean')}
                value={getAttr('testimonialLayout') || 'card'}
                options={[
                    { label: __('Card (Default)', 'puroclean'), value: 'card' },
                    { label: __('Minimal', 'puroclean'), value: 'minimal' },
                    { label: __('Featured', 'puroclean'), value: 'featured' },
                    { label: __('Slides', 'puroclean'), value: 'slides' }
                ]}
                onChange={(value) => setAttr('testimonialLayout', value)}
                help={__('Choose the visual style for testimonials', 'puroclean')}
            />

            <ToggleControl
                label={__('Random Order', 'puroclean')}
                checked={getAttr('testimonialRandomOrder') !== false}
                onChange={(value) => setAttr('testimonialRandomOrder', value)}
                help={__('Display testimonials in random order', 'puroclean')}
            />

            <ToggleControl
                label={__('Show Navigation Dots', 'puroclean')}
                checked={getAttr('testimonialShowDots') !== false}
                onChange={(value) => setAttr('testimonialShowDots', value)}
                help={__('Show pagination dots below testimonials', 'puroclean')}
            />

            <ToggleControl
                label={__('Show Navigation Arrows', 'puroclean')}
                checked={getAttr('testimonialShowArrows') || false}
                onChange={(value) => setAttr('testimonialShowArrows', value)}
                help={__('Show arrow navigation buttons', 'puroclean')}
            />

            <ToggleControl
                label={__('Enable Autoplay', 'puroclean')}
                checked={getAttr('testimonialAutoplay') || false}
                onChange={(value) => setAttr('testimonialAutoplay', value)}
                help={__('Automatically rotate through testimonials', 'puroclean')}
            />

            {getAttr('testimonialAutoplay') && (
                <NumberControl
                    label={__('Autoplay Speed (ms)', 'puroclean')}
                    value={getAttr('testimonialAutoplaySpeed') || 5000}
                    onChange={(value) => setAttr('testimonialAutoplaySpeed', parseInt(value) || 5000)}
                    min={1000}
                    max={15000}
                    step={500}
                    help={__('Time between slides in milliseconds', 'puroclean')}
                />
            )}
        </PanelBody>
    );
};

/**
 * Get testimonial props from block attributes
 * @param {Object} attributes - Block attributes
 * @param {String} prefix - Optional prefix for attribute names
 * @returns {Object} - Props object for TestimonialsCarousel component
 */
export const getTestimonialProps = (attributes, prefix = '') => {
    const getAttr = (name, defaultValue) => {
        const value = attributes[`${prefix}${name}`];
        return value !== undefined ? value : defaultValue;
    };

    return {
        testimonialType: getAttr('testimonialType', 'client'),
        count: getAttr('testimonialCount', 3),
        layout: getAttr('testimonialLayout', 'card'),
        randomOrder: getAttr('testimonialRandomOrder', true),
        showDots: getAttr('testimonialShowDots', true),
        showArrows: getAttr('testimonialShowArrows', false),
        autoplay: getAttr('testimonialAutoplay', false),
        autoplaySpeed: getAttr('testimonialAutoplaySpeed', 5000),
        isEditor: true
    };
};

/**
 * Default attributes for testimonials
 * Add these to your block.json attributes
 */
export const testimonialAttributes = {
    testimonialType: {
        type: 'string',
        default: 'client'
    },
    testimonialCount: {
        type: 'number',
        default: 3
    },
    testimonialLayout: {
        type: 'string',
        default: 'card'
    },
    testimonialRandomOrder: {
        type: 'boolean',
        default: true
    },
    testimonialShowDots: {
        type: 'boolean',
        default: true
    },
    testimonialShowArrows: {
        type: 'boolean',
        default: false
    },
    testimonialAutoplay: {
        type: 'boolean',
        default: false
    },
    testimonialAutoplaySpeed: {
        type: 'number',
        default: 5000
    }
};

export default { TestimonialControls, getTestimonialProps, testimonialAttributes }; 